import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BLANK = { key: "", headline: "", body: "", audience: "all", provider_key: "",
                starts_on: "", ends_on: "", link: "" };

export default function Announcements() {
  const [board, setBoard] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    apiFetch("/announcements").then(async (r) => {
      if (r.status === 403) { setError("You do not have permission to read configuration."); return; }
      if (!r.ok) { setError("Unable to load announcements."); return; }
      setBoard(await r.json());
    });
  }, []);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    load();
  }, [navigate, load]);

  const say = async (path, body) => {
    const r = await apiFetch(path, { method: "POST",
                                     body: body ? JSON.stringify(body) : undefined });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) {
      const detail = out.detail;
      setNotice(typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg || d).join("; ")
          : detail?.message || "That did not work.");
      return null;
    }
    setNotice(out.message || null);
    load();
    return out;
  };

  const write = async () => {
    const payload = { ...draft,
                      key: draft.key || draft.headline.toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-").slice(0, 40).replace(/^-|-$/g, "") };
    const out = await say("/announcements", payload);
    if (out) setDraft(BLANK);
  };

  if (error) {
    return <p className="p-6 text-sm text-red-900" data-testid="announcements-error">{error}</p>;
  }
  if (!board) {
    return <p className="p-6 text-sm text-slate-600" data-testid="announcements-loading">Loading…</p>;
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 lg:p-6" data-testid="announcements-page">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold" data-testid="announcements-title">Announcements</h1>
        <p className="text-sm text-slate-600">{board.definition}</p>
        <p className="text-xs text-slate-600" data-testid="announcements-method">{board.method}</p>
      </header>

      {notice && <p className="text-sm text-amber-900" data-testid="announcements-notice">{notice}</p>}

      <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
               aria-labelledby="write-heading" data-testid="announcement-form">
        <h2 id="write-heading" className="text-base font-semibold md:text-lg">
          Write an announcement
        </h2>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1 md:col-span-2">
            <label htmlFor="ann-headline" className="text-sm font-medium">Headline</label>
            <Input id="ann-headline" data-testid="announcement-headline" value={draft.headline}
                   onChange={(e) => setDraft({ ...draft, headline: e.target.value })} />
          </div>
          <div className="space-y-1 md:col-span-2">
            <label htmlFor="ann-body" className="text-sm font-medium">
              What do people need to know?
            </label>
            <textarea id="ann-body" data-testid="announcement-body" rows={3}
                      className="w-full rounded-md border px-3 py-2 text-sm"
                      value={draft.body}
                      onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-audience" className="text-sm font-medium">Who sees it</label>
            <select id="ann-audience" data-testid="announcement-audience"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={draft.audience}
                    onChange={(e) => setDraft({ ...draft, audience: e.target.value })}>
              <option value="all">Everyone signed in</option>
              <option value="staff">Staff</option>
              <option value="administrators">Administrators only</option>
            </select>
            <p className="text-xs text-slate-600">{board.audience_note}</p>
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-provider" className="text-sm font-medium">
              Published by (optional provider)
            </label>
            <Input id="ann-provider" data-testid="announcement-provider"
                   value={draft.provider_key}
                   onChange={(e) => setDraft({ ...draft, provider_key: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-start" className="text-sm font-medium">Show from</label>
            <Input id="ann-start" type="date" data-testid="announcement-start"
                   value={draft.starts_on}
                   onChange={(e) => setDraft({ ...draft, starts_on: e.target.value })} />
          </div>
          <div className="space-y-1">
            <label htmlFor="ann-end" className="text-sm font-medium">Show until</label>
            <Input id="ann-end" type="date" data-testid="announcement-end" value={draft.ends_on}
                   onChange={(e) => setDraft({ ...draft, ends_on: e.target.value })} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" data-testid="announcement-save"
                  disabled={!draft.headline.trim() || !draft.body.trim()} onClick={write}>
            Save as draft
          </Button>
          <Button asChild size="sm" variant="ghost" data-testid="announcement-home-preview">
            <Link to="/">See the portal home</Link>
          </Button>
        </div>
      </section>

      <section aria-labelledby="board-heading" data-testid="announcement-board">
        <h2 id="board-heading" className="mb-2 text-base font-semibold md:text-lg">
          {board.totals.published} live · {board.totals.drafts} draft(s) ·{" "}
          {board.totals.retired} withdrawn
        </h2>
        <ul className="space-y-2">
          {board.rows.map((row) => (
            <li key={row.id} className="rounded-md border border-slate-200 bg-white px-4 py-3
                space-y-1" data-testid={`announcement-${row.key}-${row.config_version}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${
                  row.status === "published"
                    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                    : row.status === "retired"
                      ? "border-slate-300 bg-slate-50 text-slate-700"
                      : "border-amber-300 bg-amber-50 text-amber-900"}`}
                      data-testid={`announcement-status-${row.id}`}>
                  {row.status}
                </span>
                <span className="text-sm font-medium">{row.headline}</span>
                <span className="text-xs text-slate-600">
                  v{row.config_version} · {row.audience}
                  {row.provider_key ? ` · ${row.provider_key}` : ""}
                </span>
              </div>
              <p className="text-sm text-slate-600">{row.body}</p>
              <p className="text-xs text-slate-600">
                {row.starts_on ? `from ${row.starts_on}` : "no start date"}
                {row.ends_on ? ` until ${row.ends_on}` : ""}
                {row.author_email ? ` · written by ${row.author_email}` : ""}
              </p>
              <div className="flex gap-2 pt-1">
                {row.status !== "published" && row.status !== "retired" && (
                  <Button size="sm" data-testid={`announcement-publish-${row.id}`}
                          onClick={() => say(`/announcements/${row.id}/publish`)}>
                    Publish it
                  </Button>
                )}
                {row.status === "published" && (
                  <Button size="sm" variant="outline"
                          data-testid={`announcement-withdraw-${row.id}`}
                          onClick={() => say(`/announcements/${row.id}/withdraw`)}>
                    Withdraw it
                  </Button>
                )}
              </div>
            </li>
          ))}
          {board.rows.length === 0 && (
            <li className="text-sm text-slate-600" data-testid="announcement-board-empty">
              Nothing has been written yet.
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
